import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { BlockRenderer } from '../components/blocks/BlockComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DynamicPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPage = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`${BACKEND_URL}/api/pages/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setPage(data);
        } else if (response.status === 404) {
          setError('Page not found');
        } else {
          setError('Failed to load page');
        }
      } catch (err) {
        setError('Failed to load page');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) {
      loadPage();
    }
  }, [slug]);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">404</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <a href="/" className="text-primary hover:underline">Go back home</a>
        </div>
      </div>
    );
  }

  if (!page || !page.blocks || page.blocks.length === 0) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{page?.title || 'Page'}</h1>
          <p className="text-muted-foreground">This page is empty. Add content in the CMS.</p>
        </div>
      </div>
    );
  }

  // Sort blocks by order and render
  const sortedBlocks = [...page.blocks].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="min-h-screen" data-testid={`dynamic-page-${slug}`}>
      {/* SEO Meta - would need react-helmet for proper implementation */}
      {sortedBlocks.map((block) => (
        <BlockRenderer key={block.id} block={block} />
      ))}
    </div>
  );
};

export default DynamicPage;
